/**
 * How one interface carries one VLAN.
 *
 * This is a property of the *membership*, not of the port. It is deliberately derived from
 * `isTagged`/`isNative` — which is what the bridge MIB reports for a single membership — and
 * never from a port mode.
 *
 * The distinction is not academic. The API used to send a `portMode` per membership, and it
 * could not be right: a port's mode follows from its whole membership set, so a trunk's
 * untagged native VLAN reported itself as `access` beside the same port's tagged VLANs
 * reporting `trunk`. Three separate places in the VLAN tab keyed off that value and all three
 * were wrong in the same way — the native VLAN never got its "N" badge, and every trunk was
 * listed as an access port for the VLAN it carries untagged. One tested function now, so the
 * next reader has nothing to re-derive.
 */
export type VlanMembershipKind = 'tagged' | 'native' | 'access';

export interface VlanMembershipFacts {
  isTagged: boolean;
  isNative: boolean;
}

/**
 * Classify a single (interface, VLAN) membership.
 *
 * `tagged` wins over `native`: a port can carry a VLAN tagged *and* have it as its PVID on
 * some platforms, and in that case what matters to a reader is that frames arrive tagged.
 */
export function classifyVlanMembership(m: VlanMembershipFacts): VlanMembershipKind {
  if (m.isTagged) return 'tagged';
  if (m.isNative) return 'native';
  return 'access';
}
